import React, { Fragment, useState } from "react";
import Breadcrumb from "../../components/Reusable/Breadcrumb";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Col, Row } from "react-bootstrap";
import Close from "@mui/icons-material/Close";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import { useDispatch } from "react-redux";
import { fetchCreateReceipt } from "../../actions/catalog";
import { useNavigate } from "react-router-dom";
import { t } from "i18next"; // Import t function for translation
import { toast } from "react-toastify";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  getFileTooLargeMessage,
  isFileTooLarge,
  MAX_UPLOAD_FILE_SIZE_LABEL,
} from "../../utils/fileUpload";

const formatFileSize = (bytes = 0) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState({
    accountType: 1,
    invoiceNumber: "",
    invoiceCopyNumber: "",
    invoiceDate: "",
    dateOnReceiptCopy: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCopyReceipt = Number(data.accountType) === 2;
  const isSubmitDisabled =
    isSubmitting ||
    !data.invoiceDate ||
    !data.invoiceNumber?.trim() ||
    !selectedFile ||
    (isCopyReceipt &&
      (!data.invoiceCopyNumber?.trim() || !data.dateOnReceiptCopy));

  const handleChange = (event) => {
    setData((prevUser) => ({
      ...prevUser,
      [event.target.name]: event.target.value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    // Ne dozvoljavamo veliki multipart upload da uopšte krene ka browser/backend sloju.
    if (isFileTooLarge(file)) {
      setSelectedFile(null);
      toast.error(getFileTooLargeMessage(), { theme: "colored" });
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const submitHandleForm = async (e) => {
    e.preventDefault();

    // Validacija
    if (isSubmitting) {
      return;
    }
    if (!data.invoiceNumber) {
      toast.error(t("checkout.invoiceNumberError"), { theme: "colored" });
      return;
    }
    if (!data.invoiceDate) {
      toast.error(t("checkout.invoiceNumberDate"), { theme: "colored" });
      return;
    }
    if (!selectedFile) {
      toast.error(t("checkout.invoiceImage"), { theme: "colored" });
      return;
    }
    if (isFileTooLarge(selectedFile)) {
      setSelectedFile(null);
      toast.error(getFileTooLargeMessage(), { theme: "colored" });
      return;
    }
    if (isCopyReceipt && !data.invoiceCopyNumber) {
      toast.error(t("checkout.invoiceNumberCopyError"), { theme: "colored" });
      return;
    }
    if (isCopyReceipt && !data.dateOnReceiptCopy) {
      toast.error(t("checkout.invoiceNumberCopyDate"), { theme: "colored" });
      return;
    }

    // FormData
    const formData = new FormData();
    formData.append("isCopy", isCopyReceipt);
    formData.append("receiptNumber", data.invoiceNumber);
    formData.append("receiptCopyNumber", data.invoiceCopyNumber);
    formData.append("dateOnReceipt", data.invoiceDate);
    formData.append("dateOnReceiptCopy", data.dateOnReceiptCopy);
    formData.append("receiptImage", selectedFile);

    setIsSubmitting(true);

    try {
      await dispatch(fetchCreateReceipt(formData, navigate));
    } catch (err) {
      // Ako backend/proxy ipak odbije veličinu, uklanjamo stale File iz forme.
      if (err?.response?.status === 413) {
        setSelectedFile(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="checkout-main-container">
      <Breadcrumb title={t("checkout.title")} returnPage="/cart" />
      <div className="checkout-list-container">
        <form onSubmit={submitHandleForm}>
          <div className="checkout-choose-type-cart">
            <div className="title-checkout-info">
              {t("checkout.chooseReceiptType")}
            </div>
            <FormControl>
              <RadioGroup
                row
                name="accountType"
                onChange={handleChange}
                value={data.accountType}
              >
                <FormControlLabel
                  value={1}
                  control={<Radio />}
                  label={t("checkout.originalReceipt")}
                />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label={t("checkout.copyReceipt")}
                />
              </RadioGroup>
            </FormControl>
            <div className="checkout-other-form-data mt-3">
              <Row>
                <Col lg={12}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="sr"
                  >
                    <DatePicker
                      format="DD.MM.YYYY"
                      label={t("checkout.selectReceiptDate")}
                      value={
                        data.invoiceDate
                          ? dayjs(data.invoiceDate, "YYYY-MM-DD")
                          : null
                      }
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "invoiceDate",
                            value: newValue
                              ? newValue.format("YYYY-MM-DD")
                              : "",
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "filled",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Col>
                <Col lg={12}>
                  <TextField
                    className="w-100 mt-3"
                    label={t("checkout.invoiceNumber")}
                    variant="filled"
                    name="invoiceNumber"
                    onChange={handleChange}
                    value={data.invoiceNumber}
                  />
                </Col>
                {isCopyReceipt && (
                  <Fragment>
                    <Col lg={12}>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="sr"
                      >
                        <DatePicker
                          className="mt-3"
                          format="DD.MM.YYYY"
                          label={t("checkout.selectReceiptCopyDate")}
                          value={
                            data.dateOnReceiptCopy
                              ? dayjs(data.dateOnReceiptCopy, "YYYY-MM-DD")
                              : null
                          }
                          onChange={(newValue) => {
                            handleChange({
                              target: {
                                name: "dateOnReceiptCopy",
                                value: newValue
                                  ? newValue.format("YYYY-MM-DD")
                                  : "",
                              },
                            });
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "filled",
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Col>
                    <Col lg={12}>
                      <TextField
                        className="w-100 mt-3"
                        label={t("checkout.copyReceiptNumber")}
                        variant="filled"
                        name="invoiceCopyNumber"
                        onChange={handleChange}
                        value={data.invoiceCopyNumber}
                      />
                    </Col>
                  </Fragment>
                )}
                <Col lg={12} className="mt-3">
                  <div className="checkout-holder-image">
                    <div className="checkout-holder-image-title">
                      {t("checkout.addReceiptImage")}
                    </div>
                    <div className="checkout-holder-image-info">
                      Maksimalna veličina slike je {MAX_UPLOAD_FILE_SIZE_LABEL}.
                    </div>

                    {!selectedFile ? (
                      <>
                        <input
                          type="file"
                          accept="image/*,.heic,.HEIC"
                          id="upload-file"
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                          disabled={isSubmitting}
                        />
                        <label className="upload-file" htmlFor="upload-file">
                          <Button component="span" disabled={isSubmitting}>
                            {t("checkout.addPhoto")}
                          </Button>
                        </label>
                      </>
                    ) : (
                      <div className="uploaded-image-container">
                        <div className="uploaded-image-file">
                          <div className="uploaded-image-file-icon">
                            <DescriptionOutlined />
                          </div>
                          <div className="uploaded-image-file-content">
                            <div className="uploaded-image-file-label">
                              Dokument je zakacen
                            </div>
                            <div className="uploaded-image-file-name">
                              {selectedFile?.name}
                            </div>
                            <div className="uploaded-image-file-label">
                              {formatFileSize(selectedFile?.size)}
                            </div>
                          </div>
                          <div className="remove-image-checkout">
                            <Close
                              onClick={
                                isSubmitting ? undefined : handleRemoveFile
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="checkout-other-form-data-button ">
            <Button
              type="submit"
              fullWidth
              size="small"
              disabled={isSubmitDisabled}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isSubmitting ? "Slanje..." : t("checkout.submit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
